if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.disabled || this.variantIdInput.disabled || this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const quantityInput = this.form.elements.namedItem('quantity');
        if (quantityInput) {
          if (quantityInput.type !== 'hidden') quantityInput.required = true;
          const quantity = Number(quantityInput.value);
          const min = Number(quantityInput.min) || 1;
          const max = quantityInput.hasAttribute('max') ? Number(quantityInput.max) : Infinity;
          const step = Number(quantityInput.step) || 1;
          if (!Number.isInteger(quantity) || quantity < min || quantity > max || (quantity - min) % step !== 0 || !quantityInput.checkValidity()) {
            quantityInput.reportValidity();
            return;
          }
        }

        this.handleErrorMessage();
        this.error = false;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        return fetch(`${routes.cart_add_url}`, config)
          .then(async (response) => {
            const data = await response.json();
            if (!response.ok && (!data.status || response.status >= 500)) {
              throw new Error(`Cart add failed with status ${response.status}`);
            }
            return data;
          })
          .then(async (response) => {
            if (response.status) {
              const message = response.description || response.message || window.cartStrings.error;
              // Shopify can add the available quantity even when cart/add returns 422.
              try {
                await this.refreshCartAfterError(formData.get('id'));
              } catch (error) {
                console.error(error);
              }
              await publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message || message,
              });
              this.handleErrorMessage(message, typeof response.errors !== 'object');

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            await publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response,
            });
            CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
            await this.renderCart(response);
          })
          .catch((e) => {
            const message = window.cartStrings.error;
            this.handleErrorMessage(message, true);
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: message,
              message,
            }).catch(console.error);
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (!this.error || this.submitButton.disabled) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      async refreshCartAfterError(productVariantId) {
        const cartUrl = new URL(routes.cart_url, window.location.origin);
        const cartJsonUrl = new URL(cartUrl);
        cartJsonUrl.pathname = cartJsonUrl.pathname.replace(/\/$/, '') + '.js';
        const sectionsUrl = new URL(cartUrl);
        if (this.cart) {
          sectionsUrl.searchParams.set('sections', this.cart.getSectionsToRender().map(section => section.id).join(','));
        }
        const readJson = async url => {
          const response = await fetch(url, { headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error(`Cart error recovery failed with status ${response.status}`);
          return response.json();
        };
        const [cartData, sections] = await Promise.all([
          readJson(cartJsonUrl),
          this.cart ? readJson(sectionsUrl) : Promise.resolve(null),
        ]);
        if (!Array.isArray(cartData.items) || !Number.isInteger(cartData.item_count)) {
          throw new Error('Cart error recovery returned an invalid cart state');
        }
        await publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-form',
          productVariantId,
          cartData,
        });
        const item = cartData.items.find(item => String(item.variant_id ?? item.id) === String(productVariantId));
        if (this.cart && (this.cart.tagName === 'CART-DRAWER' || item)) {
          await this.cart.renderContents({ ...item, id: productVariantId, sections }, { open: false });
        }
      }

      async renderCart(response) {
        const quickAddModal = this.closest('quick-add-modal');
        const startMarker = CartPerformance.createStartingMarker('add:paint-updated-sections');
        // Keep quick-add visible until rendering succeeds, so failures have a visible error message.
        await this.cart.renderContents(response, { open: !quickAddModal });
        CartPerformance.measureFromMarker('add:paint-updated-sections', startMarker);

        if (quickAddModal) {
          const modalClosed = new Promise((resolve) => {
            document.body.addEventListener('modalClosed', () => setTimeout(resolve), { once: true });
          });
          quickAddModal.hide(true);
          await modalClosed;
          this.cart.open();
        }
      }

      handleErrorMessage(errorMessage = false, force = false) {
        if (this.hideErrors && !force && errorMessage) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        clearTimeout(this.errorMessageTimeout);
        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
        this.errorMessage.textContent = errorMessage || '';

        if (errorMessage) {
          this.errorMessageTimeout = setTimeout(() => this.handleErrorMessage(), 3000);
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
